import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

import { useCms } from '~/components/hooks/useCms';
import { usePolicy } from '~/components/PolicyContainer';
import type { ActivePolicyCategoriesResponse } from '~/server_shared/openapi/data-contracts';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled, reportAxiosError } from '~/Utils';

// null (not []) when no provider is mounted: the visibility hook uses that to fall back to
// the claim payload's embedded list on claim surfaces, where no provider exists.
const ActivePolicyCategoriesContext = createContext<string[] | null>(null);

/**
 * Fetches the policy-category keys matching the wizard's current policy.
 *
 * Mounted once (inside the FNOL screen's PolicyContextProvider) and keyed on the policy's
 * identity, so every policy change — attach on mount, search selection, replace/detach —
 * refreshes the set through this one effect. The FE never evaluates conditions: the backend
 * computes, this just carries the answer. Fetch failure (reported, without a dialog), no
 * policy, or a manually filled policy — which has no row to ask about — all give the empty
 * set (policy-limited fields hidden — fail-safe; submission is enforced server-side
 * regardless).
 */
export const ActivePolicyCategoriesProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { policy } = usePolicy() as {
    policy?: { id?: number | string; is_manual?: boolean; last_update_date?: string };
  };
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { userOrganization } = useCms();
  const isPolicyCategoriesEnabled = isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.POLICY_CATEGORIES);

  const [activeCategoryIds, setActiveCategoryIds] = useState<string[]>([]);

  const policyId = policy?.id;
  const policyLastUpdate = policy?.last_update_date;
  // A loss taken manually has no policy row: `handleInsuredDetailsFilled` mints the policy
  // client-side with `id: uuidv4()`. That id is truthy, so a presence-only guard would send it
  // to `/<int:policy_id>/active_policy_categories`, which cannot match a uuid — a guaranteed
  // 404 whose report surfaces as the generic error modal over the FNOL form.
  const isManualPolicy = !!policy?.is_manual;

  useEffect(() => {
    if (!isPolicyCategoriesEnabled || !policyId || isManualPolicy) {
      setActiveCategoryIds([]);
      return;
    }
    let isStale = false;
    // Clear before the request, not only after it: until the new policy's answer lands the
    // previous policy's categories would otherwise keep its fields visible and editable —
    // the permissive direction, the opposite of the fail-safe this provider promises.
    setActiveCategoryIds([]);
    axios
      .get<ActivePolicyCategoriesResponse>(`/api/v1/policies/${policyId}/active_policy_categories`)
      .then(({ data }) => {
        if (!isStale) {
          setActiveCategoryIds(data?.active_policy_category_ids ?? []);
        }
      })
      .catch((error) => {
        // `openDialogError: false` — this fetch is the provider's own background work, not
        // something the user asked for, so a failure must not put a modal in front of whatever
        // they were doing. Sentry still gets it, and the empty set below is the fail-safe.
        reportAxiosError(error, false);
        if (!isStale) {
          setActiveCategoryIds([]);
        }
      });
    return () => {
      isStale = true;
    };
  }, [isPolicyCategoriesEnabled, policyId, policyLastUpdate, isManualPolicy]);

  return (
    <ActivePolicyCategoriesContext.Provider value={activeCategoryIds}>
      {children}
    </ActivePolicyCategoriesContext.Provider>
  );
};

/** The wizard's active policy-category keys, or null outside the provider (claim surfaces,
 *  which read the claim payload instead). */
export const useActivePolicyCategories = (): string[] | null => useContext(ActivePolicyCategoriesContext);
